import {
  SubscriptionFlowDefinition,
  type FlowAnswers,
  type CrossSellConfig,
  type FlowFieldRequirements,
  type RecapPolicy,
} from './SubscriptionFlowDefinition'
import type { AvailableBundlesParams } from '@/services/subscription'

/**
 * Regional health mutual "mutuelle Auvergne-Rhône-Alpes" (`/mutuelle-aura`,
 * subcategory AURA).
 *
 * The decision tree (DecisionTree::Aura) consults optical + dental to flag the
 * recommended formula; both formulas (Primordiale / Renforcée) come back from
 * `available_bundles`, so the results screen shows the two offers and lets the
 * user pick. Cross-sell add-ons (Sécurité / Indispensable) come from each base
 * bundle's upsells, priced as upsell − base — the same AVE add-ons as the health
 * journeys, and choosing one bundles a free exam-insurance line.
 *
 * An annual cover paid upfront or monthly, renewing tacitly, with the STUD'LIFE
 * club reassurance block on the recap. The period is the standard start date over
 * `duration` months (fixed to 12) and the price is the standard monthly/annual
 * rate, so neither `period()` nor `displayPrice()` is overridden.
 */

/** Front `opticalExpenses` answer → backend `optical` param (AURA never sends 'none'). */
const OPTICAL_EXPENSES_TO_OPTICAL: Record<string, AvailableBundlesParams['optical']> = {
  high: 'often',
  low: 'rarely',
}

/** Front `dentalNeeds` answer → backend `dental` param (AURA never sends 'yesProsthesis'). */
const DENTAL_NEEDS_TO_DENTAL: Record<string, AvailableBundlesParams['dental']> = {
  yes: 'yes',
  no: 'no',
}

export class RegionalAuraFlow extends SubscriptionFlowDefinition {
  readonly product = 'regionalAura' as const
  readonly subcategory = 'AURA' as const

  /** Source of truth: DecisionTree::Aura on the backend. */
  readonly planReference: Record<string, number> = {
    primordiale: 11,
    renforcee: 12,
  }

  readonly fields: FlowFieldRequirements = { ssn: true, banking: true }

  /** Yearly-renewing cover paid upfront or monthly; shows the club block. */
  readonly recap: RecapPolicy = {
    showPaymentSelector: true,
    showClub: true,
    tacitRow: 'yes',
    priceMode: 'duration',
  }

  /** AVE add-ons (Sécurité / Indispensable) from each formula's upsells; choosing
   *  one bundles a free exam-insurance line. */
  readonly crossSell: CrossSellConfig = { answerKey: 'crossSell', bundlesExamInsurance: false }

  bundlesParams(a: FlowAnswers): AvailableBundlesParams | null {
    if (!a.opticalExpenses || !a.dentalNeeds) return null
    return {
      subcategory: 'AURA',
      optical: OPTICAL_EXPENSES_TO_OPTICAL[a.opticalExpenses],
      dental: DENTAL_NEEDS_TO_DENTAL[a.dentalNeeds],
    }
  }
}
